import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { nightShiftBreakdown } from "@/lib/nightShift";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    UploadSimple,
    FileCsv,
    CheckCircle,
    Warning,
    XCircle,
    Info,
    ArrowLeft,
    Receipt,
    DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Pre-filled CSV template matching the importer's expected column order.
// Three sample rows (the ones the user explicitly requested), ISO dates,
// and example values across every field so admins see the shape clearly.
const TEMPLATE_HEADERS = [
    "Technician Name", "Email", "Week Start", "Week End",
    "Regular Hours", "Overtime Hours", "Night Shift Hours",
    "Night Shift Differential Rate", "Extraordinary Hours",
    "Hourly Rate", "Deductions", "Notes",
];
const TEMPLATE_ROWS = [
    ["Daniela Garro",       "negracasta14@gmail.com",        "2026-05-11", "2026-05-17", "40", "5",  "0", "0.00", "0", "17", "0",  "IAD-39"],
    ["Scarleth Bautista",   "michellebautista504@gmail.com", "2026-05-11", "2026-05-17", "40", "10", "0", "0.00", "0", "17", "0",  "IAD-39"],
    ["Caroline Montenegro", "caro_0907@hotmail.com",         "2026-05-11", "2026-05-17", "30", "0",  "4", "2.50", "6", "17", "25", "IAD-39 — night shift Wed"],
];

function csvEscape(value) {
    const s = String(value ?? "");
    // Quote when needed (commas, quotes, newlines). Double-up internal quotes.
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function buildTemplateCsv() {
    const lines = [TEMPLATE_HEADERS, ...TEMPLATE_ROWS]
        .map((row) => row.map(csvEscape).join(","));
    // UTF-8 BOM + CRLF line endings → opens cleanly in Excel on Windows + macOS.
    return "\uFEFF" + lines.join("\r\n") + "\r\n";
}

function downloadTemplateCsv() {
    const blob = new Blob([buildTemplateCsv()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payroll-csv-template.csv";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
    }, 1000);
}

// Recalc total pay client-side so admins see the impact of edits instantly.
// Matches backend calculate_total_pay() exactly. Extraordinary hours are
// paid at the regular hourly rate.
function recalcTotalPay(row) {
    const reg = Number(row.regular_hours) || 0;
    const ot = Number(row.overtime_hours) || 0;
    const rate = Number(row.hourly_rate) || 0;
    const extra = Number(row.extraordinary_hours) || 0;
    const diff = Number(row.night_shift_diff) || 0;
    const ded = Number(row.deductions) || 0;
    return Math.round((reg * rate + ot * rate * 1.5 + extra * rate + diff - ded) * 100) / 100;
}

// Night Shift Differential = reg night hrs × rate + OT night hrs × rate × 1.5
// (a fixed $/hr amount, never a percentage). Mirrors backend night_shift_diff_dollars().
function recalcNightDiff(row) {
    return nightShiftBreakdown(row).total;
}

const STATUS_META = {
    ready: { label: "Ready", className: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
    duplicate: { label: "Duplicate week", className: "bg-amber-100 text-amber-800 border-amber-200", icon: Warning },
    not_found: { label: "Technician not found", className: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
    invalid: { label: "Invalid data", className: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
};

function StatusPill({ status }) {
    const m = STATUS_META[status] || STATUS_META.invalid;
    const Icon = m.icon;
    return (
        <Badge
            className={`${m.className} border text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1`}
            data-testid={`row-status-${status}`}
        >
            <Icon size={11} weight="bold" /> {m.label}
        </Badge>
    );
}

export default function AdminImportCsv() {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [rows, setRows] = useState(null);          // null = no preview yet
    const [selected, setSelected] = useState({});     // index -> bool
    const [overrideDuplicate, setOverrideDuplicate] = useState({}); // index -> bool
    const [activeSites, setActiveSites] = useState([]);

    const summary = useMemo(() => {
        if (!rows) return null;
        const s = { ready: 0, duplicate: 0, not_found: 0, invalid: 0, unknown_site: 0 };
        rows.forEach((r) => {
            s[r.status] = (s[r.status] || 0) + 1;
            if (r.site_is_unknown) s.unknown_site += 1;
        });
        return s;
    }, [rows]);

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".csv")) {
            toast.error("Please choose a .csv file");
            return;
        }
        setFile(f);
        setRows(null);
        setSelected({});
        setOverrideDuplicate({});
    };

    const handlePreview = async () => {
        if (!file) {
            toast.error("Choose a CSV file first");
            return;
        }
        setPreviewing(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/reports/import/preview", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setRows(data.rows);
            setActiveSites(data.active_sites || []);
            // Auto-select all rows that are READY to import.
            const initSel = {};
            data.rows.forEach((r) => { if (r.status === "ready") initSel[r.index] = true; });
            setSelected(initSel);
            toast.success(`Parsed ${data.total} row(s)`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setPreviewing(false);
        }
    };

    const updateRow = (idx, patch) => {
        setRows((prev) => prev.map((r, i) => {
            if (i !== idx) return r;
            const merged = { ...r, ...patch };
            // Editing night hours, the $/hr rate or OT hours (which decide how many
            // night hours earn ×1.5) always re-derives the differential; typing
            // directly into Night diff ($) is a manual override.
            if ("night_shift_hours" in patch || "night_shift_differential_rate" in patch || "overtime_hours" in patch) {
                merged.night_shift_diff = recalcNightDiff(merged);
            }
            merged.total_pay = recalcTotalPay(merged);
            return merged;
        }));
    };

    const toggleSelected = (idx) => {
        setSelected((s) => ({ ...s, [idx]: !s[idx] }));
    };

    const toggleOverride = (idx) => {
        setOverrideDuplicate((s) => {
            const next = { ...s, [idx]: !s[idx] };
            // When toggling override ON for a duplicate, auto-select it too.
            if (next[idx]) setSelected((sel) => ({ ...sel, [idx]: true }));
            return next;
        });
    };

    const selectedCount = useMemo(
        () => (rows ? rows.filter((r) => selected[r.index]).length : 0),
        [rows, selected]
    );

    const handleCommit = async () => {
        if (!rows) return;
        const toSend = rows
            .filter((r) => selected[r.index])
            .filter((r) => {
                // Block rows that are not_found/invalid (no tech id, errors); enforce duplicate override.
                if (!r.technician_id) return false;
                if (r.status === "duplicate" && !overrideDuplicate[r.index]) return false;
                return true;
            })
            .map((r) => ({
                technician_id: r.technician_id,
                week_start: r.week_start,
                week_end: r.week_end,
                hourly_rate: Number(r.hourly_rate) || 0,
                regular_hours: Number(r.regular_hours) || 0,
                overtime_hours: Number(r.overtime_hours) || 0,
                extraordinary_hours: Number(r.extraordinary_hours) || 0,
                night_shift_hours: Number(r.night_shift_hours) || 0,
                night_shift_differential_rate: Number(r.night_shift_differential_rate) || 0,
                night_shift_diff: Number(r.night_shift_diff) || 0,
                deductions: Number(r.deductions) || 0,
                total_pay: recalcTotalPay(r),
                site: r.site || "",
                notes: r.notes || "",
                override_duplicate: !!overrideDuplicate[r.index],
            }));

        if (!toSend.length) {
            toast.error("Nothing to import — select at least one ready/overridden row");
            return;
        }

        setCommitting(true);
        try {
            const { data } = await api.post("/reports/import/commit", { rows: toSend });
            const msg = `Imported ${data.created_count} report(s)` +
                (data.skipped_duplicate_count ? ` · skipped ${data.skipped_duplicate_count} duplicate(s)` : "");
            toast.success(msg);
            // Reset on success
            setFile(null);
            setRows(null);
            setSelected({});
            setOverrideDuplicate({});
            if (fileRef.current) fileRef.current.value = "";
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setCommitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        Admin · Bulk Import
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Import Payroll CSV
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm max-w-2xl">
                        Upload a weekly payroll timesheet exported from Excel or Connecteam. We'll match technicians by email
                        (or by name if email is missing), flag duplicate weeks, and let you review every row before saving.
                    </p>
                </div>
                <Link
                    to="/admin/reports"
                    className="text-sm font-semibold text-[#0066D9] hover:underline inline-flex items-center gap-1.5"
                    data-testid="back-to-reports-link"
                >
                    <ArrowLeft size={16} /> Back to reports
                </Link>
            </div>

            {/* Upload card */}
            <Card className="border-[#E2E8F0]" data-testid="csv-upload-card">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2 text-[#003366] font-semibold" style={{ fontFamily: "Outfit" }}>
                        <FileCsv size={20} weight="bold" /> Step 1 · Choose CSV file
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <Input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFileChange}
                            className="h-11 cursor-pointer border-[#E2E8F0] focus-visible:ring-[#0066D9] file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-[#0066D9] file:text-white file:font-medium file:cursor-pointer"
                            data-testid="csv-file-input"
                        />
                        <Button
                            onClick={handlePreview}
                            disabled={!file || previewing}
                            className="h-11 bg-[#003366] hover:bg-[#002244] text-white font-medium shrink-0"
                            data-testid="csv-preview-button"
                        >
                            <UploadSimple size={16} className="mr-1.5" />
                            {previewing ? "Parsing…" : "Preview rows"}
                        </Button>
                    </div>
                    <div className="flex items-center justify-between gap-3 pt-1">
                        <p className="text-xs text-slate-500">
                            Don't have a CSV yet? Start from our template.
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={downloadTemplateCsv}
                            className="h-9 border-[#0066D9] text-[#0066D9] hover:bg-[#0066D9]/5 font-medium shrink-0"
                            data-testid="csv-template-download-button"
                        >
                            <DownloadSimple size={15} className="mr-1.5" /> Download CSV Template
                        </Button>
                    </div>
                    <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex gap-2">
                        <Info size={16} className="shrink-0 mt-0.5 text-slate-400" />
                        <div>
                            <span className="font-semibold text-slate-700">Expected columns:</span>{" "}
                            Technician Name · Email · Week Start · Week End · Regular Hours · Overtime Hours ·
                            Night Shift Hours · Night Shift Differential Rate · Extraordinary Hours · Hourly Rate · Deductions · Notes.{" "}
                            Night Shift Differential = Night Shift Hours × Night Shift Differential Rate (a fixed $/hour
                            amount, not a percentage; blank = $0.00). Night hours that fall in overtime
                            (up to the week's Overtime Hours) earn the rate × 1.5.{" "}
                            Dates like <code className="font-mono bg-white px-1 py-0.5 rounded border">Monday, May 11, 2026</code>
                            {" "}or <code className="font-mono bg-white px-1 py-0.5 rounded border">2026-05-11</code> both work.
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Preview + Commit */}
            {rows && (
                <Card className="border-[#E2E8F0]" data-testid="csv-preview-card">
                    <CardContent className="p-0">
                        {/* Summary bar */}
                        <div className="p-4 sm:p-6 border-b border-[#E2E8F0] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-2 text-[#003366] font-semibold" style={{ fontFamily: "Outfit" }}>
                                <Receipt size={20} weight="bold" /> Step 2 · Review &amp; commit
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs" data-testid="csv-summary">
                                <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    {summary.ready} ready
                                </Badge>
                                <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                                    {summary.duplicate} duplicate
                                </Badge>
                                <Badge className="bg-red-100 text-red-800 border border-red-200">
                                    {summary.not_found} not found
                                </Badge>
                                {summary.unknown_site > 0 && (
                                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-testid="summary-unknown-site">
                                        {summary.unknown_site} unknown site
                                    </Badge>
                                )}
                                {summary.invalid > 0 && (
                                    <Badge className="bg-red-100 text-red-800 border border-red-200">
                                        {summary.invalid} invalid
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {/* Row list — card layout on mobile, denser on desktop */}
                        <div className="divide-y divide-[#E2E8F0]">
                            {rows.map((r) => {
                                const editable = r.status === "ready" || (r.status === "duplicate" && overrideDuplicate[r.index]);
                                const blocked = r.status === "not_found" || r.status === "invalid";
                                return (
                                    <div
                                        key={r.index}
                                        className={`p-4 sm:p-5 ${blocked ? "bg-red-50/40" : ""}`}
                                        data-testid={`csv-row-${r.index}`}
                                    >
                                        {/* Row header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                                            <div className="flex items-start gap-3 min-w-0">
                                                {!blocked && (
                                                    <input
                                                        type="checkbox"
                                                        checked={!!selected[r.index]}
                                                        onChange={() => toggleSelected(r.index)}
                                                        className="mt-1 h-4 w-4 accent-[#0066D9] cursor-pointer"
                                                        data-testid={`csv-row-${r.index}-select`}
                                                    />
                                                )}
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-[#003366] truncate">
                                                        {r.technician_name || r.csv_technician_name || "—"}
                                                    </div>
                                                    <div className="text-xs text-slate-500 truncate">
                                                        {r.csv_email || "no email in CSV"} · row {r.row_number}
                                                    </div>
                                                </div>
                                            </div>
                                            <StatusPill status={r.status} />
                                        </div>

                                        {/* Errors */}
                                        {r.errors?.length > 0 && (
                                            <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-900">
                                                <div className="font-semibold mb-1">Errors in this row:</div>
                                                <ul className="list-disc list-inside space-y-0.5">
                                                    {r.errors.map((e) => <li key={`${r.index}-${e}`}>{e}</li>)}
                                                </ul>
                                            </div>
                                        )}

                                        {/* Not found hint */}
                                        {r.status === "not_found" && (
                                            <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-2.5 text-xs text-red-900">
                                                No matching technician in the portal. Invite them first or fix the
                                                spelling/email, then re-upload the CSV.
                                            </div>
                                        )}

                                        {/* Duplicate override */}
                                        {r.status === "duplicate" && (
                                            <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2">
                                                <Warning size={16} className="shrink-0 mt-0.5" weight="fill" />
                                                <div className="flex-1">
                                                    A report for this technician + week already exists. By default we'll
                                                    skip it.{" "}
                                                    <label className="inline-flex items-center gap-1.5 cursor-pointer font-semibold">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!overrideDuplicate[r.index]}
                                                            onChange={() => toggleOverride(r.index)}
                                                            className="h-3.5 w-3.5 accent-[#E6002C] cursor-pointer"
                                                            data-testid={`csv-row-${r.index}-override`}
                                                        />
                                                        Create a second report anyway
                                                    </label>
                                                </div>
                                            </div>
                                        )}

                                        {/* Unknown site warning */}
                                        {r.site_is_unknown && (
                                            <div
                                                className="mb-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900 flex items-start gap-2"
                                                data-testid={`csv-row-${r.index}-unknown-site`}
                                            >
                                                <Warning size={16} className="shrink-0 mt-0.5" weight="fill" />
                                                <div>
                                                    The site <span className="font-mono font-semibold">"{r.csv_site_text}"</span> is not in
                                                    your managed sites list. Pick a site below or add it first under
                                                    <span className="font-semibold"> Admin · Sites</span>.
                                                </div>
                                            </div>
                                        )}

                                        {/* Editable grid */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                                            <Field label="Week start" value={r.week_start} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { week_start: v })}
                                                type="date" testid={`csv-row-${r.index}-week-start`} />
                                            <Field label="Week end" value={r.week_end} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { week_end: v })}
                                                type="date" testid={`csv-row-${r.index}-week-end`} />
                                            <Field label="Reg hrs" value={r.regular_hours} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { regular_hours: v })}
                                                type="number" step="0.25" testid={`csv-row-${r.index}-reg-hours`} />
                                            <Field label="OT hrs" value={r.overtime_hours} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { overtime_hours: v })}
                                                type="number" step="0.25" testid={`csv-row-${r.index}-ot-hours`} />
                                            <Field label="Extra hrs" value={r.extraordinary_hours} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { extraordinary_hours: v })}
                                                type="number" step="0.25" testid={`csv-row-${r.index}-extra-hours`} />
                                            <Field label="Rate ($/hr)" value={r.hourly_rate} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { hourly_rate: v })}
                                                type="number" step="0.01" testid={`csv-row-${r.index}-rate`} />
                                            <Field label="Night hrs" value={r.night_shift_hours} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { night_shift_hours: v })}
                                                type="number" step="0.25" testid={`csv-row-${r.index}-night-hours`} />
                                            <Field label="Night rate ($/hr)" value={r.night_shift_differential_rate} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { night_shift_differential_rate: v })}
                                                type="number" step="0.01" testid={`csv-row-${r.index}-night-rate`} />
                                            <Field label="Night diff ($)" value={r.night_shift_diff} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { night_shift_diff: v })}
                                                type="number" step="0.01" testid={`csv-row-${r.index}-night-diff`} />
                                            <Field label="Deductions ($)" value={r.deductions} disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { deductions: v })}
                                                type="number" step="0.01" testid={`csv-row-${r.index}-deductions`} />
                                            <SiteSelect
                                                label="Site"
                                                value={r.site}
                                                disabled={!editable}
                                                onChange={(v) => updateRow(r.index, { site: v })}
                                                options={activeSites}
                                                testid={`csv-row-${r.index}-site`}
                                            />
                                            <div className="col-span-2 sm:col-span-1 flex flex-col">
                                                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                                                    Total pay
                                                </label>
                                                <div
                                                    className="h-9 px-2 rounded border border-[#E2E8F0] bg-slate-50 text-sm font-bold text-[#003366] flex items-center"
                                                    data-testid={`csv-row-${r.index}-total`}
                                                >
                                                    ${Number(r.total_pay).toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Commit bar */}
                        <div className="p-4 sm:p-5 border-t border-[#E2E8F0] bg-slate-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sticky bottom-0">
                            <div className="text-sm text-slate-700">
                                <span className="font-bold text-[#003366]">{selectedCount}</span> row(s) selected for import.
                                {summary.not_found > 0 && (
                                    <span className="text-red-700 ml-2">
                                        {summary.not_found} skipped (technician not found).
                                    </span>
                                )}
                            </div>
                            <Button
                                onClick={handleCommit}
                                disabled={committing || selectedCount === 0}
                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium h-10"
                                data-testid="csv-commit-button"
                            >
                                {committing ? "Importing…" : `Import ${selectedCount} report(s)`}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function Field({ label, value, onChange, disabled, type = "text", step, testid }) {
    return (
        <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                {label}
            </label>
            <input
                type={type}
                step={step}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="h-9 px-2 rounded border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066D9] focus:border-transparent disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                data-testid={testid}
            />
        </div>
    );
}

function SiteSelect({ label, value, onChange, disabled, options, testid }) {
    // Include the current value as an option even if it's not in the active
    // sites list — preserves legacy data shown in edit mode.
    const optionsWithCurrent = [...options];
    if (value && !options.some((o) => o.name === value)) {
        optionsWithCurrent.push({ id: `__legacy_${value}`, name: value, legacy: true });
    }
    return (
        <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
                {label}
            </label>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="h-9 px-2 rounded border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066D9] focus:border-transparent disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed bg-white"
                data-testid={testid}
            >
                <option value="">— Select site —</option>
                {optionsWithCurrent.map((s) => (
                    <option key={s.id} value={s.name}>
                        {s.name}{s.legacy ? " (legacy)" : ""}
                    </option>
                ))}
            </select>
        </div>
    );
}
